import { Fragment, useEffect } from 'react';

import {
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
} from '@material-ui/core';

import {
  FilterEditorComponentProps,
  SimpleAutocomplete,
} from '@spotify/confidence-core-react';
import { EntityFragment } from '@spotify/confidence-graphql';

import { useEntities } from '../../hooks';

const Container = styled('div')({
  maxHeight: 300,
  maxWidth: 300,
  overflowY: 'auto',
});

export const EntityFilterEditor = ({
  value,
  onValueChange,
  onClose,
}: FilterEditorComponentProps<string[]>) => {
  const { entities, hasNextPage, nextPage } = useEntities();

  useEffect(() => {
    if (hasNextPage) {
      nextPage();
    }
  }, [hasNextPage, nextPage]);

  return (
    <Container>
      <SimpleAutocomplete.Root
        multiple
        items={
          entities?.map((entity: EntityFragment) => ({
            value: entity.name,
            label: entity.displayName,
          })) ?? []
        }
        value={value ?? []}
        onValueChange={onValueChange}
        // when someone selects from the list, close the editor
        onSelect={() => {
          onClose();
          return false;
        }}
      >
        {({ items, getInputProps, getListboxProps, getOptionProps }) => (
          <Fragment>
            <SimpleAutocomplete.Input
              {...getInputProps()}
              placeholder="Search entities..."
              fullWidth
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              margin="dense"
            />
            <SimpleAutocomplete.Empty />

            <List {...getListboxProps()}>
              {items.map((item, index) => (
                <ListItem
                  key={item.value}
                  button
                  disableGutters
                  {...getOptionProps(index)}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={value?.includes(item.value) ?? false}
                      onClick={e => e.stopPropagation()}
                      onChange={e => {
                        if (e.target.checked) {
                          onValueChange([...(value ?? []), item.value]);
                        } else {
                          onValueChange(
                            value?.filter(v => v !== item.value) ?? [],
                          );
                        }
                      }}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      noWrap: true,
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Fragment>
        )}
      </SimpleAutocomplete.Root>
    </Container>
  );
};
